import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Loader2, ExternalLink, Upload, Car, Star, Eye, EyeOff, Check, ShieldCheck, Phone, Megaphone, Save, Sparkles, Instagram, Image as ImageIcon } from "lucide-react";
import { api, resolveImg } from "../../lib/site";
import { useAuth } from "../../context/AuthContext";

const empty = {
  name: "", category: "car", body_type: "", image_url: "", fuel: "Petrol",
  spec: "", seating: "", price_12hr: "", price_24hr: "", description: "",
  featured: false, available: true, sort_order: 0,
};

const inputCls = "w-full border border-white/15 bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-volt";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("vehicles");

  const doLogout = () => { logout(); nav("/admin"); };

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <Toaster theme="dark" position="top-right" richColors />
      <header className="sticky top-0 z-30 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1300px] items-center justify-between px-5 py-4 md:px-8">
          <div>
            <h1 className="font-heading text-lg font-black uppercase tracking-tight">RAC-K Admin</h1>
            <p className="text-xs text-white/40">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-volt sm:inline-flex">
              View Site <ExternalLink size={13} />
            </a>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:border-white/40 hover:text-white">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-[1300px] gap-1 px-5 md:px-8">
          {[["vehicles", "Vehicles", Car], ["reviews", "Reviews", Star], ["verifications", "Verifications", ShieldCheck], ["gallery", "Gallery", ImageIcon], ["settings", "Settings", Megaphone]].map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === key ? "border-volt text-volt" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-5 py-8 md:px-8">
        {tab === "vehicles" ? <VehiclesPanel /> : tab === "reviews" ? <ReviewsPanel /> : tab === "verifications" ? <VerificationsPanel /> : tab === "gallery" ? <GalleryPanel /> : <SettingsPanel />}
      </main>
    </div>
  );
}

function ImageField({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="col-span-2">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-white/50">Vehicle Image</label>
      <div className="flex gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden border border-white/15 bg-ink">
          {value ? <img src={resolveImg(value)} alt="preview" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-[10px] text-white/30">No image</div>}
        </div>
        <div className="flex-1 space-y-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-bold uppercase tracking-wider text-white hover:border-volt hover:text-volt disabled:opacity-60"
          >
            {uploading ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} Upload Photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="image-file-input" />
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid="field-image"
            className={inputCls}
            placeholder="…or paste an image URL"
          />
        </div>
      </div>
    </div>
  );
}

function VehiclesPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/vehicles").then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => setModal({ mode: "add", data: { ...empty, sort_order: vehicles.length + 1 } });
  const openEdit = (v) => setModal({ mode: "edit", data: { ...v } });
  const set = (k, val) => setModal((m) => ({ ...m, data: { ...m.data, [k]: val } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data } = modal;
    try {
      const payload = { ...data, sort_order: Number(data.sort_order) || 0 };
      if (modal.mode === "add") { await api.post("/vehicles", payload); toast.success("Vehicle added"); }
      else { await api.put(`/vehicles/${data.id}`, payload); toast.success("Vehicle updated"); }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleAvailable = async (v) => {
    try {
      await api.put(`/vehicles/${v.id}`, { ...v, available: !v.available });
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Fleet ({vehicles.length})</h2>
          <p className="text-sm text-white/50">Add, edit, mark booked, and remove vehicles.</p>
        </div>
        <button onClick={openAdd} data-testid="add-vehicle-btn" className="inline-flex items-center gap-2 bg-volt px-5 py-3 text-xs font-bold uppercase tracking-wider text-ink hover:bg-volt-hover">
          <Plus size={16} /> Add Vehicle
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-volt" size={30} /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((v) => (
            <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="flex flex-col border border-white/10 bg-ink-surface">
              <div className="relative aspect-video overflow-hidden bg-black">
                {v.image_url && <img src={resolveImg(v.image_url)} alt={v.name} className="h-full w-full object-cover" />}
                <span className="absolute left-2 top-2 bg-volt px-2 py-0.5 text-[10px] font-bold uppercase text-ink">{v.category}</span>
                {!v.available && <span className="absolute right-2 top-2 bg-destructive px-2 py-0.5 text-[10px] font-bold uppercase">Booked</span>}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="font-heading text-lg font-bold">{v.name}</h3>
                <p className="text-xs text-white/40">{v.body_type} · {v.spec} · {v.seating}</p>
                <p className="mt-2 text-sm font-bold text-volt">{v.price_24hr}{v.price_12hr ? ` · ${v.price_12hr}` : ""}</p>
                <button
                  onClick={() => toggleAvailable(v)}
                  data-testid={`toggle-available-${v.id}`}
                  className={`mt-3 inline-flex w-fit items-center gap-1.5 border px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide ${v.available ? "border-volt/40 text-volt" : "border-destructive/50 text-destructive"}`}
                >
                  {v.available ? "Available" : "Booked"} — tap to toggle
                </button>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(v)} data-testid={`edit-${v.id}`} className="flex flex-1 items-center justify-center gap-1.5 border border-white/15 py-2.5 text-xs font-bold uppercase hover:border-volt hover:text-volt">
                    <Pencil size={13} /> Edit
                  </button>
                  <button onClick={() => remove(v)} data-testid={`delete-${v.id}`} className="flex items-center justify-center border border-white/15 px-3 py-2.5 text-white/60 hover:border-destructive hover:text-destructive">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="vehicle-modal">
          <form onSubmit={save} className="my-8 w-full max-w-lg border border-white/10 bg-ink-surface p-6 md:p-8">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight">{modal.mode === "add" ? "Add Vehicle" : "Edit Vehicle"}</h3>
              <button type="button" onClick={() => setModal(null)} data-testid="modal-close"><X size={22} className="text-white/60 hover:text-white" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" span><input required value={modal.data.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name" className={inputCls} /></Field>
              <Field label="Category">
                <select value={modal.data.category} onChange={(e) => set("category", e.target.value)} data-testid="field-category" className={inputCls}>
                  <option value="car">Car</option>
                  <option value="bike">Bike</option>
                </select>
              </Field>
              <Field label="Body Type"><input value={modal.data.body_type} onChange={(e) => set("body_type", e.target.value)} placeholder="SUV / Scooter" className={inputCls} /></Field>
              <Field label="Fuel"><input value={modal.data.fuel} onChange={(e) => set("fuel", e.target.value)} className={inputCls} /></Field>
              <Field label="Transmission / CC"><input value={modal.data.spec} onChange={(e) => set("spec", e.target.value)} placeholder="Manual / 110 CC" className={inputCls} /></Field>
              <Field label="Seating"><input value={modal.data.seating} onChange={(e) => set("seating", e.target.value)} placeholder="5 Seater" className={inputCls} /></Field>
              <Field label="Sort Order"><input type="number" value={modal.data.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={inputCls} /></Field>
              <Field label="Price 12 hrs"><input value={modal.data.price_12hr} onChange={(e) => set("price_12hr", e.target.value)} placeholder="₹1,200 / 12 hrs" className={inputCls} /></Field>
              <Field label="Price 24 hrs"><input value={modal.data.price_24hr} onChange={(e) => set("price_24hr", e.target.value)} placeholder="₹2,200 / 24 hrs" className={inputCls} /></Field>
              <ImageField value={modal.data.image_url} onChange={(url) => set("image_url", url)} />
              <Field label="Description" span><textarea value={modal.data.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} /></Field>
            </div>
            <div className="mt-4 flex gap-6">
              <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={modal.data.available} onChange={(e) => set("available", e.target.checked)} className="accent-volt" /> Available</label>
              <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={modal.data.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-volt" /> Featured</label>
            </div>
            <button type="submit" disabled={saving} data-testid="modal-save" className="mt-6 flex w-full items-center justify-center gap-2 bg-volt py-3.5 text-sm font-bold uppercase tracking-wider text-ink hover:bg-volt-hover disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : modal.mode === "add" ? "Add Vehicle" : "Save Changes"}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/reviews/all"), api.get("/settings")])
      .then(([r, s]) => { setReviews(r.data); setEnabled(s.data.reviews_enabled); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleSection = async () => {
    try {
      const { data } = await api.put("/settings", { reviews_enabled: !enabled });
      setEnabled(data.reviews_enabled);
      toast.success(data.reviews_enabled ? "Reviews section shown on site" : "Reviews section hidden from site");
    } catch { toast.error("Update failed"); }
  };

  const patch = async (r, fields) => {
    try { await api.put(`/reviews/${r.id}`, fields); load(); } catch { toast.error("Update failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.name}?`)) return;
    try { await api.delete(`/reviews/${r.id}`); toast.success("Review deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const pending = reviews.filter((r) => !r.approved).length;

  return (
    <>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Reviews ({reviews.length})</h2>
          <p className="text-sm text-white/50">{pending} awaiting approval · approve, hide or delete.</p>
        </div>
        <button
          onClick={toggleSection}
          data-testid="reviews-toggle"
          className={`inline-flex items-center gap-2 border px-4 py-2.5 text-xs font-bold uppercase tracking-wider ${enabled ? "border-volt bg-volt text-ink" : "border-white/20 text-white/70"}`}
        >
          <span className={`h-2.5 w-2.5 rounded-full ${enabled ? "bg-ink" : "bg-white/40"}`} />
          Reviews Section: {enabled ? "ON" : "OFF"}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-volt" size={30} /></div>
      ) : reviews.length === 0 ? (
        <p className="py-16 text-center text-white/50">No reviews yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-white/10 bg-ink-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold uppercase tracking-wide">{r.name}</span>
                    <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-volt text-volt" : "text-white/25"} />)}</div>
                  </div>
                  <div className="mt-1 flex gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${r.approved ? "text-volt" : "text-yellow-400"}`}>{r.approved ? "Approved" : "Pending"}</span>
                    {r.hidden && <span className="text-[10px] font-bold uppercase tracking-wide text-white/40">Hidden</span>}
                  </div>
                </div>
                <button onClick={() => remove(r)} data-testid={`review-delete-${r.id}`} className="text-white/50 hover:text-destructive"><Trash2 size={16} /></button>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/75">"{r.text}"</p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => patch(r, { approved: !r.approved })}
                  data-testid={`review-approve-${r.id}`}
                  className={`flex flex-1 items-center justify-center gap-1.5 border py-2 text-xs font-bold uppercase tracking-wide ${r.approved ? "border-white/15 text-white/60 hover:border-white/40" : "border-volt bg-volt text-ink"}`}
                >
                  <Check size={13} /> {r.approved ? "Unapprove" : "Approve"}
                </button>
                <button
                  onClick={() => patch(r, { hidden: !r.hidden })}
                  data-testid={`review-hide-${r.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 border border-white/15 py-2 text-xs font-bold uppercase tracking-wide text-white/70 hover:border-white/40"
                >
                  {r.hidden ? <><Eye size={13} /> Unhide</> : <><EyeOff size={13} /> Hide</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function AuthImage({ vid, kind, label }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let objUrl;
    api.get(`/verifications/${vid}/doc/${kind}`, { responseType: "blob" })
      .then((res) => { objUrl = URL.createObjectURL(res.data); setUrl(objUrl); })
      .catch(() => setErr(true));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [vid, kind]);
  if (err) return null;
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" data-testid={`vdoc-${kind}-${vid}`} className="group relative block overflow-hidden border border-white/15 bg-ink">
      <span className="absolute left-2 top-2 z-10 bg-black/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{label}</span>
      {url ? <img src={url} alt={label} className="h-32 w-full object-cover transition-transform group-hover:scale-105" /> : <div className="flex h-32 items-center justify-center"><Loader2 className="animate-spin text-white/40" size={18} /></div>}
    </a>
  );
}

function VerificationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/verifications").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (v, status) => {
    try { await api.put(`/verifications/${v.id}`, { status }); load(); } catch { toast.error("Update failed"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete verification from ${v.name}?`)) return;
    try { await api.delete(`/verifications/${v.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const pending = items.filter((v) => v.status !== "verified").length;

  return (
    <>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Document Verifications ({items.length})</h2>
        <p className="text-sm text-white/50">{pending} awaiting review · renter-submitted licence & ID photos for faster pickup.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-volt" size={30} /></div>
      ) : items.length === 0 ? (
        <p className="py-16 text-center text-white/50">No document submissions yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((v) => (
            <div key={v.id} data-testid={`admin-verification-${v.id}`} className="border border-white/10 bg-ink-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold uppercase tracking-wide">{v.name}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${v.status === "verified" ? "text-volt" : "text-yellow-400"}`}>{v.status}</span>
                  </div>
                  <a href={`tel:${v.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-volt"><Phone size={11} /> {v.phone}</a>
                  {v.note && <p className="mt-1 text-xs text-white/50">"{v.note}"</p>}
                </div>
                <button onClick={() => remove(v)} data-testid={`verification-delete-${v.id}`} className="text-white/50 hover:text-destructive"><Trash2 size={16} /></button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <AuthImage vid={v.id} kind="license" label="Licence" />
                {v.id_path ? <AuthImage vid={v.id} kind="id" label="Govt ID" /> : <div className="flex h-32 items-center justify-center border border-dashed border-white/10 text-[11px] text-white/30">No ID uploaded</div>}
              </div>
              <button
                onClick={() => setStatus(v, v.status === "verified" ? "pending" : "verified")}
                data-testid={`verification-toggle-${v.id}`}
                className={`mt-4 flex w-full items-center justify-center gap-1.5 border py-2.5 text-xs font-bold uppercase tracking-wide ${v.status === "verified" ? "border-white/15 text-white/60 hover:border-white/40" : "border-volt bg-volt text-ink"}`}
              >
                <Check size={13} /> {v.status === "verified" ? "Mark Pending" : "Mark Verified"}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const OFFER_PRESETS = ["#D6FF00", "#FF3B30", "#FF7A00", "#2563EB", "#7C3AED", "#10B981", "#EC4899", "#FACC15"];

function SettingsPanel() {
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");
  const [ends, setEnds] = useState("");
  const [color, setColor] = useState("#D6FF00");
  const [instagram, setInstagram] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setEnabled(data.offer_enabled);
      setText(data.offer_text || "");
      setEnds(data.offer_ends_at ? data.offer_ends_at.slice(0, 16) : "");
      setColor(data.offer_color || "#D6FF00");
      setInstagram(data.instagram_url || "");
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { offer_enabled: enabled, offer_text: text, offer_ends_at: ends, offer_color: color, instagram_url: instagram });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const previewFg = (() => {
    const h = color.replace("#", "");
    if (h.length < 6) return "#060806";
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#060806" : "#FFFFFF";
  })();

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-volt" size={30} /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-panel">
      <div>
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Promo Banner</h2>
        <p className="text-sm text-white/50">Show a dismissible offer bar at the top of your website for weekend or festival deals.</p>
      </div>

      <div className="border border-white/10 bg-ink-surface p-6">
        <label className="flex cursor-pointer items-center justify-between">
          <span className="text-sm font-bold uppercase tracking-wide text-white">Show banner on site</span>
          <button type="button" onClick={() => setEnabled((v) => !v)} data-testid="offer-enabled-toggle" className={`relative h-7 w-12 rounded-full transition-colors ${enabled ? "bg-volt" : "bg-white/15"}`} aria-pressed={enabled}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-ink transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </label>

        <div className="mt-6">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Offer text</label>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={160} data-testid="offer-text-input" className={inputCls} placeholder="e.g. Festive Deal: 15% off all weekend bookings — enquire now!" />
          <p className="mt-1 text-right text-[11px] text-white/30">{text.length}/160</p>
        </div>

        <div className="mt-4">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Banner colour</label>
          <div className="flex flex-wrap items-center gap-2">
            {OFFER_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                data-testid={`offer-color-${c.replace("#", "")}`}
                aria-label={`Colour ${c}`}
                className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color.toUpperCase() === c ? "border-white" : "border-white/20"}`}
                style={{ backgroundColor: c }}
              />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="offer-color-input" className="h-8 w-10 cursor-pointer border border-white/15 bg-ink p-0.5" />
            <input value={color} onChange={(e) => setColor(e.target.value)} className={`${inputCls} w-28`} />
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Ends on (optional — shows a countdown)</label>
          <div className="flex items-center gap-3">
            <input type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} data-testid="offer-ends-input" className={`${inputCls} [color-scheme:dark]`} />
            {ends && <button type="button" onClick={() => setEnds("")} data-testid="offer-ends-clear" className="shrink-0 border border-white/15 px-3 py-2.5 text-xs font-bold uppercase text-white/60 hover:border-white/40 hover:text-white">Clear</button>}
          </div>
          <p className="mt-1 text-[11px] text-white/30">Leave empty for an always-on banner. When the time passes, the banner hides automatically.</p>
        </div>

        <div className="mt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-white/50">Preview</span>
          <div className="mt-2 flex h-10 items-center justify-center gap-3 px-4" style={{ backgroundColor: color, color: previewFg }} data-testid="offer-preview">
            <span className="flex items-center gap-2 truncate">
              <Sparkles size={15} />
              <span className="truncate text-xs font-bold uppercase tracking-wide sm:text-sm">{text || "Your offer text will appear here"}</span>
            </span>
            {ends && <span className="hidden shrink-0 items-center gap-1.5 pl-3 text-xs font-bold uppercase sm:flex" style={{ borderLeft: `1px solid ${previewFg}40` }}>Ends {new Date(ends).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</span>}
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Instagram</h2>
        <p className="text-sm text-white/50">Link shown in the footer and gallery "Follow us" button.</p>
      </div>
      <div className="border border-white/10 bg-ink-surface p-6">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-white/50">Instagram profile URL</label>
        <div className="flex items-center gap-2">
          <Instagram size={18} className="text-volt" />
          <input value={instagram} onChange={(e) => setInstagram(e.target.value)} data-testid="instagram-url-input" className={inputCls} placeholder="https://www.instagram.com/yourhandle/" />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="offer-save" className="flex items-center justify-center gap-2 bg-volt px-6 py-3 text-sm font-bold uppercase tracking-wider text-ink hover:bg-volt-hover disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={15} /> Save Settings</>}
      </button>
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState("");
  const [caption, setCaption] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => { setLoading(true); api.get("/gallery").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const add = async () => {
    if (!image) { toast.error("Upload or paste a photo first"); return; }
    setAdding(true);
    try {
      await api.post("/gallery", { image_url: image, caption, sort_order: items.length + 1 });
      toast.success("Photo added");
      setImage(""); setCaption("");
      load();
    } catch { toast.error("Add failed"); }
    finally { setAdding(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/gallery/${it.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const dragIdx = useRef(null);
  const onDrop = async (dropIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === dropIdx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    setItems(next);
    try {
      await api.put("/gallery-reorder", next.map((i) => i.id));
      toast.success("Order updated");
    } catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Instagram Gallery ({items.length})</h2>
        <p className="text-sm text-white/50">Upload photos for the "From our Instagram" grid. Drag photos to reorder.</p>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 border border-white/10 bg-ink-surface p-6 sm:grid-cols-2">
        <ImageField value={image} onChange={setImage} />
        <div className="col-span-2 flex flex-col gap-3 sm:flex-row">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption" className={`${inputCls} flex-1`} placeholder="Caption (optional)" />
          <button onClick={add} disabled={adding} data-testid="gallery-add" className="flex items-center justify-center gap-2 bg-volt px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-ink hover:bg-volt-hover disabled:opacity-60">
            {adding ? <Loader2 className="animate-spin" size={15} /> : <><Plus size={15} /> Add Photo</>}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-volt" size={28} /></div>
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-white/50">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((it, idx) => (
            <div
              key={it.id}
              data-testid={`admin-gallery-${it.id}`}
              draggable
              onDragStart={() => (dragIdx.current = idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className="group relative aspect-square cursor-move overflow-hidden border border-white/10 bg-ink"
            >
              <img src={resolveImg(it.image_url)} alt={it.caption} className="pointer-events-none h-full w-full object-cover" />
              <span className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center bg-volt text-[11px] font-bold text-ink">{idx + 1}</span>
              {it.caption && <span className="absolute inset-x-0 bottom-0 truncate bg-ink/80 px-2 py-1 text-[10px] text-white">{it.caption}</span>}
              <button onClick={() => remove(it)} data-testid={`gallery-delete-${it.id}`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-ink/80 text-white/80 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const Field = ({ label, children, span }) => (
  <div className={span ? "col-span-2" : ""}>
    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-white/50">{label}</label>
    {children}
  </div>
);
